// File: src/components/pages/MyProgress.jsx
// Student-facing progress tracker — read-only view of all progress entries
// Uses the same card/renderer logic as StudentProgress.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Clock,
  FileText,
  RefreshCw
} from 'lucide-react';
import { progressTrackerAPI } from '../../services/api';
import '../../styles/unified.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (d) =>
  !d ? '—' : new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

// ── Scale colour — hue 0 (red) → 120 (green) as value rises 0→5 ─────────────
const scaleColor = (value) => {
  const hue = Math.round((Math.max(0, Math.min(value, 5)) / 5) * 120);
  return `hsl(${hue}, 80%, 42%)`;
};

// ── Value renderer (identical to StudentProgress.jsx) ─────────────────────────

const renderValue = (itm) => {
  const { type, value } = itm;

  if (type === 'Scale') {
    const pct = Math.round((value / 5) * 100);
    return (
      <div className="ev-bar-wrap">
        <div className="ev-bar">
          <div className="ev-bar-fill" style={{ width: `${pct}%`, backgroundColor: scaleColor(value) }} />
        </div>
        <span className="ev-score" style={{ color: scaleColor(value), fontWeight: 700 }}>
          {value} / 5
        </span>
      </div>
    );
  }
  if (type === 'Rating') {
    const pct = Math.round(((value - 1) / 4) * 100);
    return (
      <div className="ev-bar-wrap">
        <div className="ev-bar"><div className="ev-bar-fill" style={{ width: `${pct}%` }} /></div>
        <span className="ev-score">{value} / 5</span>
      </div>
    );
  }
  if (type === 'Checkbox') {
    return value
      ? <span className="ev-pill ev-pill-yes">✓ Completed</span>
      : <span className="ev-pill ev-pill-no">Pending</span>;
  }
  if (type === 'Pass/Fail') {
    return value === 'Pass'
      ? <span className="ev-pill ev-pill-pass">Pass</span>
      : value === 'Fail'
        ? <span className="ev-pill ev-pill-fail">Fail</span>
        : <span className="ev-muted">—</span>;
  }
  // Text
  return <span className="ev-note">{value || <span className="ev-muted">No comments</span>}</span>;
};

// ── MyProgress ────────────────────────────────────────────────────────────────

const MyProgress = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [entryList,   setEntryList]   = useState([]);
  const [isLoading,   setIsLoading]   = useState(false);
  const [loadError,   setLoadError]   = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());

  // ── Fetch ────────────────────────────────────────────────────────────────

  const fetchProgress = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const res = await progressTrackerAPI.getMyProgress(getAccessTokenSilently);
      if (res.success) {
        const data = res.data || [];
        setEntryList(data);
        // Auto-expand first entry on load
        if (data.length > 0) {
          setExpandedIds(new Set([data[0].progressId]));
        }
      } else {
        setLoadError('Failed to load your progress.');
      }
    } catch (err) {
      console.error('MyProgress fetch error:', err);
      setLoadError(err.message || 'Failed to load your progress.');
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => { fetchProgress(); }, [fetchProgress]);

  // ── Toggle cards ──────────────────────────────────────────────────────────

  const toggleCard = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="page-container">

      {/* Page header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '1.75rem', paddingBottom: '1rem',
        borderBottom: '2px solid #e5e7eb', flexWrap: 'wrap', gap: '0.75rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
          <TrendingUp size={28} color="#667eea" />
          <div>
            <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700, color: '#111827' }}>
              My Progress
            </h1>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
              Progress reports submitted by your instructor
            </p>
          </div>
        </div>
        <button
          onClick={fetchProgress}
          disabled={isLoading}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.4rem',
            background: '#fff', border: '1px solid #e5e7eb',
            borderRadius: '0.5rem', padding: '0.45rem 0.85rem',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            color: '#6b7280', fontSize: '0.85rem', fontWeight: 500,
            opacity: isLoading ? 0.6 : 1
          }}
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading your progress…</p>
        </div>
      )}

      {/* Error */}
      {!isLoading && loadError && (
        <div className="error-banner">
          <AlertCircle size={16} />
          <span>{loadError}</span>
          <button
            onClick={fetchProgress}
            style={{
              marginLeft: 'auto', background: '#dc2626', color: '#fff',
              border: 'none', borderRadius: '0.4rem',
              padding: '0.3rem 0.75rem', cursor: 'pointer',
              fontSize: '0.82rem', fontWeight: 600
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty */}
      {!isLoading && !loadError && entryList.length === 0 && (
        <div className="sp-entry-empty">
          <FileText size={32} style={{ color: '#cbd5e1' }} />
          <p>No progress reports yet. Your instructor's entries will appear here.</p>
        </div>
      )}

      {/* Entry list — same card structure as StudentProgress.jsx */}
      {!isLoading && !loadError && entryList.length > 0 && (
        <div className="sp-entry-list">
          {entryList.map((entry, entryIdx) => {
            const isFirst  = entryIdx === 0;
            const isOpen   = isFirst || expandedIds.has(entry.progressId);
            const isDraft  = entry.status === 'draft';

            return (
              <div
                key={entry.progressId}
                className={`sp-entry-card ${isDraft ? 'sp-entry-draft' : 'sp-entry-submitted'}`}
              >
                {/* Card header */}
                <div
                  className="sp-entry-hdr"
                  onClick={() => !isFirst && toggleCard(entry.progressId)}
                  style={{ cursor: isFirst ? 'default' : 'pointer' }}
                >
                  <div className="sp-entry-hdr-left">
                    <span className={`sp-entry-badge ${isDraft ? 'badge-draft' : 'badge-submitted'}`}>
                      {isDraft
                        ? <><Clock size={11} /> Draft</>
                        : <><CheckCircle2 size={11} /> Submitted</>}
                    </span>
                    <span className="sp-entry-level">Level {entry.progressLevel}</span>
                  </div>
                  <div className="sp-entry-hdr-right">
                    <span className="sp-entry-date">
                      {isDraft
                        ? `Saved ${formatDate(entry.updatedAt)}`
                        : `Submitted ${formatDate(entry.submittedAt)}`}
                    </span>
                    {!isFirst && (
                      <span className="sp-chevron">{isOpen ? '▼' : '▶'}</span>
                    )}
                  </div>
                </div>

                {/* Card body */}
                {isOpen && (
                  <div className="sp-entry-body">
                    {(entry.entries || []).map((tpl, ti) => (
                      <div
                        key={tpl.templateId}
                        className={`ev-section${ti > 0 ? ' ev-section-border' : ''}`}
                      >
                        <div className="ev-section-heading">{tpl.heading}</div>
                        <div className="ev-col-table">
                          <div className="ev-col-headers">
                            {(tpl.items || []).map((itm, i) => (
                              <div key={i} className="ev-col-header">{itm.item}</div>
                            ))}
                          </div>
                          <div className="ev-col-values">
                            {(tpl.items || []).map((itm, i) => (
                              <div key={i} className="ev-col-value">
                                {renderValue(itm)}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyProgress;